// src/pages/NewPropertyPage.tsx
import * as React from "react";
import {
    Stack,
    Typography,
    TextField,
    Button,
    Paper,
    MenuItem,
    Stepper,
    Step,
    StepLabel,
    Alert,
    CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { adminFetch } from "../services/adminFetch";

const steps = ["Basic info", "Pricing & status"];

// simple slugify (same style as Edit)
function slugify(s: string) {
    return (s || "")
        .toString()
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

async function jsonOrThrow(res: Response) {
    const data = await res.json().catch(() => null);
    if (!res.ok) {
        const msg = data?.error || data?.message || `HTTP ${res.status}`;
        throw new Error(msg);
    }
    return data;
}

export default function NewPropertyPage() {
    const nav = useNavigate();
    const [step, setStep] = React.useState(0);
    const [saving, setSaving] = React.useState(false);
    const [err, setErr] = React.useState<string | null>(null);

    const [form, setForm] = React.useState({
        title: "",
        city: "",
        region: "",
        status: "draft" as "draft" | "published",
        price_per_night: "",
    });

    const update = (k: keyof typeof form, v: any) =>
        setForm((f) => ({ ...f, [k]: v }));

    const next = () => setStep((s) => s + 1);
    const back = () => setStep((s) => s - 1);

    const submit = async () => {
        setSaving(true);
        try {
            const res = await adminFetch("/api/admin/properties", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...form,
                    price_per_night: form.price_per_night ? Number(form.price_per_night) : null,
                }),
            });

            const data = await res.json().catch(() => null);

            if (!res.ok) {
                console.error("Create property failed:", res.status, data);
                return;
            }

            const id = data?.item?.id || data?.id; // fallback
            if (!id) {
                console.error("No id in response:", data);
                return;
            }

            // ✅ ΒΑΛΕ ΕΔΩ ΤΟ ΣΩΣΤΟ PATH ΤΟΥ EDIT ΣΟΥ
            nav(`/admin/properties/${id}?new=1`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack spacing={3}>
            <Typography variant="h5" sx={{ fontWeight: 1000 }}>
                Add new property
            </Typography>

            {err ? <Alert severity="error">{err}</Alert> : null}

            <Stepper activeStep={step}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                {step === 0 && (
                    <Stack spacing={2}>
                        <TextField
                            label="Title"
                            value={form.title}
                            onChange={(e) => update("title", e.target.value)}
                            required
                            autoFocus
                        />

                        <TextField
                            label="City"
                            value={form.city}
                            onChange={(e) => update("city", e.target.value)}
                        />

                        <TextField
                            label="Region"
                            value={form.region}
                            onChange={(e) => update("region", e.target.value)}
                        />

                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" onClick={next} disabled={!form.title.trim()}>
                                Continue
                            </Button>
                        </Stack>
                    </Stack>
                )}

                {step === 1 && (
                    <Stack spacing={2}>
                        <TextField
                            label="Price per night (€)"
                            type="number"
                            value={form.price_per_night}
                            onChange={(e) => update("price_per_night", e.target.value)}
                        />

                        {/* optional: keep it here for UX, but backend will create draft anyway */}
                        <TextField
                            select
                            label="Status (will create as Draft first)"
                            value={form.status}
                            onChange={(e) => update("status", e.target.value as any)}
                            disabled
                            helperText="We create a draft first, then you publish from Edit when everything is ready."
                        >
                            <MenuItem value="draft">Draft</MenuItem>
                            <MenuItem value="published">Published</MenuItem>
                        </TextField>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button onClick={back} disabled={saving}>
                                Back
                            </Button>

                            <Button variant="contained" onClick={submit} disabled={saving || !form.title.trim()}>
                                {saving ? "Creating…" : "Create property"}
                            </Button>

                            {saving ? <CircularProgress size={18} /> : null}
                        </Stack>
                    </Stack>
                )}
            </Paper>
        </Stack>
    );
}
